import { createTamagui } from 'tamagui';
import { themes } from './themes';
import { defaultConfig } from '@tamagui/config/v4';
import { tamaguiLightColors, tamaguiDarkColors } from './tamagui-colors';

// Use colors from the marketing website color palette
const lightColors = tamaguiLightColors;
const darkColors = tamaguiDarkColors;

// Extend default tokens with marketing website colors
const extendedTokens = {
	...defaultConfig.tokens,
	color: {
		...defaultConfig.tokens.color,
		// Marketing website color tokens
		background: lightColors.background,
		foreground: lightColors.foreground,
		card: lightColors.card,
		cardForeground: lightColors.cardForeground,
		popover: lightColors.popover,
		popoverForeground: lightColors.popoverForeground,
		primary: lightColors.primary,
		primaryForeground: lightColors.primaryForeground,
		primaryGlow: lightColors.primaryGlow,
		secondary: lightColors.secondary,
		secondaryForeground: lightColors.secondaryForeground,
		muted: lightColors.muted,
		mutedForeground: lightColors.mutedForeground,
		accent: lightColors.accent,
		accentForeground: lightColors.accentForeground,
		destructive: lightColors.destructive,
		destructiveForeground: lightColors.destructiveForeground,
		border: lightColors.border,
		input: lightColors.input,
		ring: lightColors.ring,
		// Keep existing color scales for compatibility
		grey0: '#f9f9f9',
		grey1: '#f0f0f0',
		grey2: '#d9d9d9',
		grey3: '#bfbfbf',
		grey4: '#8c8c8c',
		grey5: '#595959',
		grey6: '#262626',
		grey7: '#1f1f1f',
		grey8: '#141414',
		grey9: '#0f0f0f',
		grey10: '#0a0a0a',
		grey11: '#050505',
		grey12: '#000000',
		blue0: '#e6f7ff',
		blue1: '#bae7ff',
		blue2: '#91d5ff',
		blue3: '#69c0ff',
		blue4: '#40a9ff',
		blue5: '#1890ff',
		blue6: '#096dd9',
		blue7: '#0050b3',
		blue8: '#003a8c',
		blue9: '#002766',
		blue10: '#001d40',
		blue11: '#001319',
		blue12: '#000a0f',
		red0: '#fff1f0',
		red1: '#ffccc7',
		red2: '#ffa39e',
		red3: '#ff7875',
		red4: '#ff4d4f',
		red5: '#f5222d',
		red6: '#cf1322',
		red7: '#a8071a',
		red8: '#820014',
		red9: '#5c0011',
		red10: '#3f000c',
		red11: '#2a0008',
		red12: '#1a0005',
		yellow0: '#feffe6',
		yellow1: '#ffffb8',
		yellow2: '#fffb8f',
		yellow3: '#fff566',
		yellow4: '#ffec3d',
		yellow5: '#fadb14',
		yellow6: '#d4b106',
		yellow7: '#ad8b00',
		yellow8: '#876800',
		yellow9: '#614700',
		yellow10: '#3d2f00',
		yellow11: '#291f00',
		yellow12: '#1a1300',
		green0: '#f6ffed',
		green1: '#d9f7be',
		green2: '#b7eb8f',
		green3: '#95de64',
		green4: '#73d13d',
		green5: '#52c41a',
		green6: '#389e0d',
		green7: '#237804',
		green8: '#135200',
		green9: '#092b00',
		green10: '#061f00',
		green11: '#041500',
		green12: '#020d00',
	},
	radius: {
		...defaultConfig.tokens.radius,
		// Custom radius values matching marketing website (--radius: 0.75rem = 12px)
		'sm': 8, // calc(var(--radius) - 4px)
		'md': 10, // calc(var(--radius) - 2px)
		'lg': 12, // var(--radius)
		'xl': 16, // calc(var(--radius) + 4px)
		'2xl': 20, // calc(var(--radius) + 8px)
	},
	fontSize: {
		...defaultConfig.tokens.fontSize,
		// Typography scale matching marketing website
		'xs': 12,
		'sm': 14,
		'base': 16,
		'lg': 18,
		'xl': 20,
		'2xl': 24,
		'3xl': 30,
		'4xl': 36,
		'5xl': 48,
	},
	fontWeight: {
		...defaultConfig.tokens.fontWeight,
		// Font weights
		light: '300',
		normal: '400',
		medium: '500',
		semibold: '600',
		bold: '700',
		extrabold: '800',
	},
	lineHeight: {
		...defaultConfig.tokens.lineHeight,
		// Line heights
		tight: 1.25,
		normal: 1.5,
		relaxed: 1.75,
	},
};

export const config = createTamagui({
	...defaultConfig,
	tokens: extendedTokens,
	// Don't override fonts - use default Tamagui fonts
	fonts: defaultConfig.fonts,
	themes: {
		...themes,
		// Override theme colors with marketing website colors
		light: {
			...themes.light,
			...lightColors,
			backgroundHover: lightColors.secondary,
			backgroundPress: lightColors.muted,
		},
		dark: {
			...themes.dark,
			...darkColors,
			backgroundHover: darkColors.secondary,
			backgroundPress: darkColors.muted,
		},
	},
	// Skip optimization for heavy components
	exclude: ['SocialMediaScheduler'],
});
